import type { TextFieldOnChangeArguments, GetOptionPropsResult, OptionProps } from 'uikit';
import { useClickOutside, scrollIntoView } from 'uikit';

import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';

import {
    AutoCompleteEnum,
    findEstimatedVariableBoundaries,
    findWordBoundaries,
    getStringWithShieldsCreator,
    NEW_LINE,
} from './helpers';

interface ISource {
    [key: string]: Array<string>;
}

export function useAutoComplete({
    source,
    field,
    dropdownRef,
    controlRef,
    isDotNotationVariables,
}: {
    source: ISource | undefined;
    field: ControllerRenderProps<FieldValues, any>;
    dropdownRef: MutableRefObject<HTMLDivElement | null>;
    controlRef: MutableRefObject<HTMLDivElement | null>;
    isDotNotationVariables: boolean;
}) {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const coloredRef = useRef<HTMLDivElement>(null);

    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [items, setItems] = useState<string[] | undefined>(undefined);

    const dropItems = useCallback(() => {
        setItems(undefined);
    }, []);

    const sortedObjectKeys = useMemo(() => Object.keys(source ?? {})?.sort((a, b) => a.localeCompare(b)), [source]);

    const getVisibleItems = useCallback(
        ({ level, firstLevelSubString = '', secondLevelSubString = '' }) => {
            if (level === AutoCompleteEnum.FIRST_LEVEL_OBJ) {
                setItems(
                    source &&
                        sortedObjectKeys?.filter((item: string) =>
                            item
                                .toLowerCase()
                                .includes(firstLevelSubString.replaceAll(NEW_LINE, '').toLocaleLowerCase()),
                        ),
                );

                return;
            }

            if (level === AutoCompleteEnum.SECOND_LEVEL_OBJ) {
                setItems(
                    source?.[firstLevelSubString]
                        ?.filter((item: string) =>
                            item
                                .toLowerCase()
                                .includes(secondLevelSubString.replaceAll(NEW_LINE, '').toLocaleLowerCase()),
                        )
                        ?.sort((a, b) => a.localeCompare(b)),
                );

                return;
            }

            dropItems();
        },
        [dropItems, sortedObjectKeys, source],
    );

    const onKeyUpHandler = useCallback(
        (agrs: TextFieldOnChangeArguments) => {
            const { e, value } = agrs;

            const { selectionStart } = e.target as HTMLInputElement;

            if (selectionStart === null || selectionStart === undefined) {
                dropItems();

                return;
            }

            const { left, right } = findWordBoundaries(value ?? '', selectionStart);

            const isVariable = value?.startsWith(AutoCompleteEnum.VARIABLE_CHAR, left);

            if (isVariable) {
                const subString = value?.substring(left + 1, right);

                if (subString?.includes(AutoCompleteEnum.VARIABLE_DIVIDER)) {
                    const subStringArr = subString?.split(AutoCompleteEnum.VARIABLE_DIVIDER);

                    getVisibleItems({
                        level: AutoCompleteEnum.SECOND_LEVEL_OBJ,
                        firstLevelSubString: subStringArr[0],
                        secondLevelSubString: subStringArr[1],
                    });

                    return;
                }

                getVisibleItems({
                    level: AutoCompleteEnum.FIRST_LEVEL_OBJ,
                    firstLevelSubString: subString,
                });
            } else {
                dropItems();
            }
        },
        [dropItems, getVisibleItems],
    );

    const finaleItems = useMemo(
        () =>
            items?.length
                ? [
                      {
                          items,
                          key: 'items',
                          groupIndex: 1,
                      },
                  ]
                : [],
        [items],
    );

    const hasItems = Boolean(items?.length);

    const getStringWithShields = useMemo(() => getStringWithShieldsCreator({ objectSource: source }), [source]);

    const itemDropdownChecker = useCallback(
        (item: string): void => {
            const fullString = field.value;
            const { selectionStart } = inputRef?.current as HTMLTextAreaElement;

            if (selectionStart === null || selectionStart === undefined) {
                return;
            }

            const { left, right } = findWordBoundaries(fullString, selectionStart);

            const dotIndex = fullString.indexOf(AutoCompleteEnum.VARIABLE_DIVIDER, left);
            const pasteIndex = right > dotIndex && dotIndex !== -1 ? dotIndex + 1 : left + 1;

            const leftSideSubString = fullString.substring(0, pasteIndex);
            const rightSideSubString = fullString.substring(selectionStart);

            const newString = getStringWithShields({
                fullStringValue: `${leftSideSubString}${item}${rightSideSubString}`,
            });

            field.onChange(newString);

            setTimeout(() => {
                let newCaretPosition;

                if (isDotNotationVariables) {
                    newCaretPosition =
                        `${leftSideSubString}${item}`.length + (dotIndex !== -1 && dotIndex < pasteIndex ? 2 : 0);
                } else {
                    newCaretPosition = `${leftSideSubString}${item}`.length + 2;
                }

                inputRef?.current?.focus();

                (inputRef?.current as HTMLTextAreaElement).setSelectionRange(newCaretPosition, newCaretPosition);
            }, 0);

            dropItems();
            setHighlightedIndex(0);
        },
        [dropItems, field, getStringWithShields, isDotNotationVariables],
    );

    const getOptionProps = ({ index, item }: OptionProps<any>): GetOptionPropsResult => {
        const key = item.id ?? item;

        return {
            onClick: () => itemDropdownChecker(item),
            onMouseEnter: () => {
                setHighlightedIndex(Math.min(Math.max(0, index), (items?.length ?? 0) - 1));
            },
            active: false,
            hovered: index === highlightedIndex,
            key,
        };
    };

    const highlightIndex = useCallback(
        (indexForHighlightGetter: (idx: number) => number) => () => {
            setHighlightedIndex((prevIndex) =>
                Math.min(Math.max(0, indexForHighlightGetter(prevIndex)), (items?.length ?? 0) - 1),
            );
        },
        [items?.length],
    );

    const checkHighlightedIndex = useCallback(
        (currentIndex: number) => () => {
            const currentIndexItem = items?.[currentIndex];

            if (currentIndexItem) {
                itemDropdownChecker(currentIndexItem);
            }
        },
        [itemDropdownChecker, items],
    );

    const deleteItemsHandler = useCallback(
        (event: KeyboardEvent) => {
            const fullString = field.value;

            const { selectionStart, selectionEnd } = inputRef?.current as HTMLTextAreaElement;

            if (selectionStart === 0 && selectionEnd === fullString?.length) {
                field.onChange('');

                return;
            }

            if (!selectionStart && selectionStart === selectionEnd) {
                return;
            }
            const findEstimatedVariable = (index: number) => findEstimatedVariableBoundaries(fullString ?? '', index);

            const removeSubstringAndUpdateSelection = (startIndex: number, endIndex: number) => {
                event.preventDefault();

                const leftSubString = fullString.substring(0, startIndex);
                const rightSubString = fullString.substring(endIndex);

                const newString = `${leftSubString}${rightSubString}`;

                field.onChange(newString);

                setTimeout(() => {
                    (inputRef?.current as HTMLTextAreaElement).setSelectionRange(startIndex, startIndex);
                }, 0);
            };

            if (selectionStart === selectionEnd) {
                const { left: estimatedVariableLeft, right: estimatedVariableRight } =
                    findEstimatedVariable(selectionStart);

                const estimatedVariable = fullString?.substring(estimatedVariableLeft, estimatedVariableRight);

                if (
                    estimatedVariable?.startsWith(AutoCompleteEnum.LEFT_BOUNDARY) &&
                    estimatedVariable?.endsWith(AutoCompleteEnum.RIGHT_BOUNDARY) &&
                    !estimatedVariable?.includes(AutoCompleteEnum.LEFT_BOUNDARY, 1) &&
                    selectionStart > estimatedVariableLeft
                ) {
                    removeSubstringAndUpdateSelection(estimatedVariableLeft, estimatedVariableRight);
                }
            } else {
                const isInsideVariable = (index: number) => {
                    const leftBoundaryIndex = fullString.indexOf(AutoCompleteEnum.LEFT_BOUNDARY, index);
                    const rightBoundaryIndex = fullString.indexOf(AutoCompleteEnum.RIGHT_BOUNDARY, index);

                    return leftBoundaryIndex > rightBoundaryIndex || leftBoundaryIndex === -1;
                };

                const { left: estimatedRangeLeft } = findEstimatedVariable(selectionStart);
                const { right: estimatedRangeRight } = findEstimatedVariable(selectionEnd);

                const leftRangeStartIndex = isInsideVariable(selectionStart) ? estimatedRangeLeft : selectionStart;
                const rightRangeStartIndex = isInsideVariable(selectionEnd) ? estimatedRangeRight : selectionEnd;

                removeSubstringAndUpdateSelection(leftRangeStartIndex, rightRangeStartIndex);
            }
        },
        [field],
    );

    const keysListeners = useMemo<{ [key: string]: any }>(
        () => ({
            ArrowUp: highlightIndex((old = 0) => old - 1),
            ArrowDown: highlightIndex((old = 0) => old + 1),
            Enter: checkHighlightedIndex(highlightedIndex),
            Backspace: {
                type: AutoCompleteEnum.IMMEDIATELY_RUN,
                action: (event: KeyboardEvent) => {
                    deleteItemsHandler(event);
                },
            },
            Delete: {
                type: AutoCompleteEnum.IMMEDIATELY_RUN,
                action: (event: KeyboardEvent) => {
                    deleteItemsHandler(event);
                },
            },
            Escape: dropItems,
        }),
        [checkHighlightedIndex, deleteItemsHandler, dropItems, highlightIndex, highlightedIndex],
    );

    const keyDownListener = useCallback(
        (e) => {
            const { code, key } = e;

            const handler = keysListeners[key] || keysListeners[code];

            if (handler?.type === AutoCompleteEnum.IMMEDIATELY_RUN) {
                handler.action(e);
            }

            if (hasItems && handler && typeof handler === 'function') {
                e.preventDefault();
                handler();
            }
        },
        [hasItems, keysListeners],
    );

    const syncScroll = useCallback(
        (target: HTMLElement | null) => (e: Event) => {
            const sourceElement = e.target as HTMLElement;

            if (target) {
                target.scrollTop = sourceElement?.scrollTop;
            }
        },
        [],
    );

    useClickOutside({
        isActive: hasItems,
        ignoreClicksInsideRefs: [dropdownRef, controlRef, inputRef],
        handler: dropItems,
    });

    useEffect(() => {
        const currentInputElement = inputRef.current;

        currentInputElement?.addEventListener('keydown', keyDownListener);

        return () => currentInputElement?.removeEventListener('keydown', keyDownListener);
    }, [hasItems, highlightIndex, keyDownListener, keysListeners]);

    useEffect(() => {
        const currentInputElement = inputRef.current;
        const coloredStringElement = coloredRef.current;

        currentInputElement?.addEventListener('scroll', syncScroll(coloredStringElement));
        coloredStringElement?.addEventListener('scroll', syncScroll(currentInputElement));

        return () => {
            currentInputElement?.removeEventListener('scroll', syncScroll(coloredStringElement));
            coloredStringElement?.removeEventListener('scroll', syncScroll(currentInputElement));
        };
    }, [syncScroll]);

    useEffect(() => {
        if (hasItems && highlightedIndex && dropdownRef.current) {
            const elements: NodeListOf<HTMLDivElement> | undefined =
                dropdownRef?.current?.querySelectorAll('div[role=option]');

            if (!!elements && dropdownRef?.current) {
                scrollIntoView(elements[highlightedIndex], dropdownRef.current);
            }
        }
    }, [dropdownRef, hasItems, highlightedIndex]);

    return {
        isOpen: hasItems,
        inputRef,
        coloredRef,
        onKeyUpHandler,
        getOptionProps,
        visibleItems: finaleItems,
        hasItems,
    };
}
